import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Package } from "lucide-react";
import Pagination from "../../components/Pagination";
import formatCurrency from "../../utils/formatCurrency";

const RecentOrders = ({ orders = [], pagination = null, onPageChange }) => {
  const navigate = useNavigate();

  const statusStyles = {
    pending_payment: "bg-orange-100 text-orange-600",
    pending: "bg-orange-100 text-orange-600",
    processing: "bg-blue-100 text-blue-600",
    shipped: "bg-blue-100 text-blue-600",
    delivered: "bg-green-100 text-green-600",
    cancelled: "bg-red-100 text-red-600",
  };

  const formatOrderStatus = (status) => {
    return (
      status
        ?.replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ") || "Unknown"
    );
  };

  // Pagination logic
  const currentPage = pagination?.current_page || 1;
  const totalPages = pagination?.last_page || 1;
  const perPage = pagination?.per_page || orders.length || 10;
  const total = pagination?.total || orders.length;

  return (
    <div className="overflow-hidden">
      <div className="py-4 sm:py-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h3 className="font-medium text-lg sm:text-xl text-[#0C0C0E]">
          Recent Orders
        </h3>
        <button
          onClick={() => navigate("/orders")}
          className="text-base font-medium text-[#728550] flex items-center gap-1 active:scale-95 transition-transform duration-100"
        >
          View All <ChevronRight size={16} />
        </button>
      </div>

      <div className="border border-[#EEF1F3] rounded-lg">
        {orders.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left shadow-sm text-xs sm:text-sm">
                <thead className="bg-[#FBF8F6] text-[#7D828D] font-medium">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 sm:py-4">Order ID</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4">Customer</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4">Amount</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4">Date</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4">Order Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EEF1F3]">
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm text-[#0C0C0E]"
                    >
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        ORD-{order.id}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        {order.user?.name || "Unknown"}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        {formatCurrency(order.payment_transaction?.amount || 0)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <span
                          className={`px-2 sm:px-3 py-1 rounded-full text-[10px] font-bold flex items-center gap-1.5 w-fit ${statusStyles[order.order_status] || "bg-gray-100 text-gray-600"}`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${order.order_status?.includes("pending") ? "bg-orange-600" : order.order_status?.includes("shipped") ? "bg-blue-600" : order.order_status?.includes("delivered") ? "bg-green-600" : "bg-gray-600"}`}
                          ></span>
                          {formatOrderStatus(order.order_status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between border-t border-[#EEF1F3] bg-[#FBF8F6] w-full">
              <span className="text-sm text-[#7D828D] mb-2 sm:mb-0">
                Showing {total ? (currentPage - 1) * perPage + 1 : 0} to{" "}
                {Math.min(currentPage * perPage, total)} of {total} orders
              </span>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
              <Package size={32} className="text-[#BFAC93]" />
            </div>
            <h4 className="font-medium text-base text-[#0C0C0E] text-center mb-2">
              No orders yet
            </h4>
            <p className="text-sm text-slate-400 text-center">
              Orders will appear here once customers place them
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentOrders;
